import './AppsFunView.css';

/**
 * AppsWrapper - Fetches and displays the Astro apps content within the main site
 */
export default class AppsWrapper {
  constructor(container) {
    this.container = container;
    this.apps = [];
    this.filteredApps = [];
    this.searchTerm = '';
    this.selectedType = 'all';

    // Fun view / Basic view toggle (fun view is the default)
    this.currentView = 'fun';

    // Fun view game state
    this.doors = [];
    this.player = null;
    this.funContainer = null;
    this.river = null;
    this.keys = { up: false, down: false, left: false, right: false };
    this.animationFrame = null;
    this.listenersAttached = false;

    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  async render() {
    this.container.innerHTML = `
      <div class="ologies-wrapper">
        <div class="ologies-content-container">
          <div class="ologies-header">
            <h1><span class="ologies-title">'Apps'</span> —our products: original, efficient, human.</h1>
          </div>

          <div class="ologies-loading">
            <p>Loading apps...</p>
          </div>
        </div>
      </div>
    `;

    try {
      // Fetch the apps data from the Astro API
      await this.fetchApps();
      this.renderPage();
    } catch (error) {
      console.error('Error loading apps:', error);
      this.showError('Failed to load apps content');
    }
  }

  async fetchApps() {
    try {
      // Determine the correct API base URL
      let apiUrl;
      const isDevelopment = window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname.includes('localhost') ||
                           window.location.port === '3000' ||
                           window.location.port === '3001' ||
                           window.location.port === '3002' ||
                           window.location.port === '3003' ||
                           window.location.port === '3004' ||
                           window.location.port === '3005' ||
                           window.location.port === '3006' ||
                           window.location.port === '3007' ||
                           window.location.port === '3008' ||
                           window.location.port === '3009' ||
                           window.location.port === '3010';

      if (isDevelopment) {
        // Development: Use the local Astro dev server
        apiUrl = 'http://localhost:4321/api/apps/VIBED_CREATIONS.json';
      } else {
        // Production: Use the actual build location
        apiUrl = '/file/ologies/api/apps/VIBED_CREATIONS.json';
      }

      // Try to fetch from the API endpoint for all apps
      const response = await fetch(apiUrl);

      if (response.ok) {
        const data = await response.json();

        if (data.apps && Array.isArray(data.apps)) {
          this.apps = data.apps.map(app => ({
            slug: app.slug,
            data: {
              title: app.title,
              subtitle: app.subtitle,
              description: app.description,
              type: app.type,
              rating: app.rating,
              status: app.status,
              publishDate: new Date(app.publishDate),
              tags: app.tags || [],
              featured: app.featured || false,
              author: app.author || 'Our Team',
              cardImage: app.cardImage || app.appIconUrl || '',
              cardTextColor: app.cardTextColor || 'white',
              appStoreUrl: app.appStoreUrl,
              websiteUrl: app.websiteUrl
            }
          }));

          // Enforce display order
          const displayOrder = ['monk-mindset', 'seena-labs', 'meadow', 'haliscope', 'fathom', 'out-of-pocket', 'dossierx', 'greencheck', 'kinterests'];
          this.apps.sort((a, b) => {
            const indexA = displayOrder.indexOf(a.slug);
            const indexB = displayOrder.indexOf(b.slug);
            // Unknown slugs go to the end
            return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
          });

          this.filteredApps = [...this.apps];
          return;
        }
      }

      // If API is not available, show error
      throw new Error(`API not available at ${apiUrl}`);

    } catch (error) {
      console.error('Failed to fetch apps:', error);
      throw error;
    }
  }

  /**
   * Render the outer page shell: header, view toggle/legend, and both views
   */
  renderPage() {
    const categories = ['all', ...new Set(this.apps.map(a => a.data.type).filter(Boolean))];

    this.container.innerHTML = `
      <div class="ologies-wrapper">
        <div class="ologies-content-container">
          <div class="ologies-header">
            <h1><span class="ologies-title">'Apps'</span> —our products: original, efficient, human.</h1>
          </div>

          <div class="creations-view-controls">
            <div class="creations-view-toggle" role="tablist" aria-label="Creations page view">
              <button type="button" class="creations-view-toggle-btn ${this.currentView === 'fun' ? 'active' : ''}" data-view="fun" role="tab" aria-selected="${this.currentView === 'fun'}">Fun view</button>
              <button type="button" class="creations-view-toggle-btn ${this.currentView === 'basic' ? 'active' : ''}" data-view="basic" role="tab" aria-selected="${this.currentView === 'basic'}">Basic view</button>
            </div>
            <div class="creations-fun-legend" id="creations-fun-legend" style="display: ${this.currentView === 'fun' ? 'flex' : 'none'};">
              <span class="creations-legend-item"><span class="creations-key">↑↓←→</span>Move</span>
              <span class="creations-legend-item"><span class="creations-key">SPACE</span>Open door</span>
            </div>
          </div>

          <div class="creations-basic-view" id="creations-basic-view" style="display: ${this.currentView === 'basic' ? 'block' : 'none'};">
            <div class="ologies-filters">
              <div class="search-container">
                <input
                  type="text"
                  id="apps-search"
                  placeholder="Search apps..."
                  value="${this.searchTerm}"
                />
              </div>
              <div class="filter-container">
                <div class="ologies-dropdown" id="apps-type-filter" tabindex="0">
                  <span class="dropdown-text">All Apps</span>
                  <span class="material-symbols-sharp dropdown-icon">keyboard_arrow_down</span>
                  <ul class="ologies-dropdown-menu" style="display: none;">
                    ${categories.map(category =>
                      `<li data-value="${category}" ${category === this.selectedType ? 'class="selected"' : ''}>
                        ${category === 'all' ? 'All Apps' : category}
                      </li>`
                    ).join('')}
                  </ul>
                </div>
              </div>
            </div>

            <div class="ologies-grid">
              ${this.filteredApps.map(app => this.renderAppCard(app)).join('')}
            </div>

            ${this.filteredApps.length === 0 ? '<div class="no-results">No apps found matching your criteria.</div>' : ''}
          </div>

          <div class="creations-fun-view" id="creations-fun-view" style="display: ${this.currentView === 'fun' ? 'block' : 'none'};"></div>
        </div>
      </div>
    `;

    this.setupEventListeners();
    this.initFunView();
  }

  renderAppCard(app) {
    const { data, slug } = app;
    const cardImage = data.cardImage || 'https://media1.giphy.com/media/v1.Y2lkPTc5MGI3NjExc2prbHNpeW9keDlnam9jdWVjOGdmaHhsZTFheThzeGt3MmJwYnM0OCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/xzgCWNwatU0bBWbV5d/giphy.gif';
    const textColor = data.cardTextColor || 'white';
    const externalUrl = data.appStoreUrl || data.websiteUrl || '';
    // Apps without a real site to send people to aren't linked anywhere for now
    const isLinkable = !!externalUrl;
    const tag = isLinkable ? 'a' : 'div';
    const linkAttrs = isLinkable
      ? `href="${externalUrl}" data-external="${externalUrl}" target="_blank" rel="noopener noreferrer"`
      : '';

    return `
      <article class="ology-card ${isLinkable ? '' : 'ology-card--disabled'} ${data.cardImage ? '' : 'ology-card--full-art'}" data-slug="${slug}" data-text-color="${textColor}">
        <${tag} class="ology-link" ${linkAttrs}>
          <div class="ology-card__background">
            <div class="ology-card__badge">
              <img
                src="${cardImage}"
                alt="Background for ${data.title}"
                class="ology-card__gif"
              />
            </div>
            <div class="ology-card__overlay"></div>
          </div>

          <div class="ology-card__content">
            <div class="ology-card__top">
              <span class="ology-card__type">${data.type || 'mobile'}</span>
              ${isLinkable ? `
              <span class="ology-card__read">
                <span class="read-text">VIEW</span>
                <span class="read-arrow material-symbols-sharp">trending_flat</span>
              </span>` : ''}
            </div>

            <div class="ology-card__bottom">
              <h3 class="ology-card__title">${data.title}</h3>
            </div>
          </div>
        </${tag}>
      </article>
    `;
  }

  setupEventListeners() {
    // Fun view / Basic view toggle
    const toggleButtons = this.container.querySelectorAll('.creations-view-toggle-btn');
    toggleButtons.forEach(btn => {
      btn.addEventListener('click', () => this.switchView(btn.dataset.view));
    });

    // Search functionality
    const searchInput = this.container.querySelector('#apps-search');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this.searchTerm = e.target.value;
        this.filterApps();
      });
    }

    // Category filter dropdown
    const categoryFilter = this.container.querySelector('#apps-type-filter');
    if (categoryFilter) {
      const dropdownText = categoryFilter.querySelector('.dropdown-text');
      const dropdownMenu = categoryFilter.querySelector('.ologies-dropdown-menu');
      const dropdownIcon = categoryFilter.querySelector('.dropdown-icon');

      categoryFilter.addEventListener('click', () => {
        const isOpen = dropdownMenu.style.display === 'block';
        dropdownMenu.style.display = isOpen ? 'none' : 'block';
        dropdownIcon.style.transform = isOpen ? 'rotate(0deg)' : 'rotate(180deg)';
      });

      // Handle dropdown item clicks
      dropdownMenu.addEventListener('click', (e) => {
        if (e.target.tagName === 'LI') {
          const value = e.target.dataset.value;
          const text = e.target.textContent;

          // Update selected state
          dropdownMenu.querySelectorAll('li').forEach(li => li.classList.remove('selected'));
          e.target.classList.add('selected');

          // Update dropdown text and filter
          dropdownText.textContent = text;
          this.selectedType = value;
          this.filterApps();

          // Close dropdown
          dropdownMenu.style.display = 'none';
          dropdownIcon.style.transform = 'rotate(0deg)';
        }
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!categoryFilter.contains(e.target)) {
          dropdownMenu.style.display = 'none';
          dropdownIcon.style.transform = 'rotate(0deg)';
        }
      });
    }

    if (!this.listenersAttached) {
      document.addEventListener('keydown', this.handleKeyDown);
      document.addEventListener('keyup', this.handleKeyUp);
      window.addEventListener('resize', this.handleResize);
      // Mobile browsers often resize the visible viewport (address bar
      // show/hide) without firing a window 'resize' event
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.handleResize);
      }
      this.listenersAttached = true;
    }
  }

  filterApps() {
    this.filteredApps = this.apps.filter(app => {
      const matchesSearch = !this.searchTerm ||
        app.data.title.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        app.data.description.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        (app.data.tags && app.data.tags.some(tag => tag.toLowerCase().includes(this.searchTerm.toLowerCase())));

      const matchesCategory = this.selectedType === 'all' || app.data.type === this.selectedType;

      return matchesSearch && matchesCategory;
    });

    // Re-render the grid
    const grid = this.container.querySelector('.ologies-grid');
    const noResults = this.container.querySelector('.no-results');

    if (grid) {
      grid.innerHTML = this.filteredApps.map(app => this.renderAppCard(app)).join('');
    }

    // Show/hide no results message
    if (noResults) {
      noResults.style.display = this.filteredApps.length === 0 ? 'block' : 'none';
    }
  }

  showError(message) {
    const isDevelopment = window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname.includes('localhost') ||
                         window.location.port === '3000' ||
                         window.location.port === '3001' ||
                         window.location.port === '3002' ||
                         window.location.port === '3003' ||
                         window.location.port === '3004' ||
                         window.location.port === '3005' ||
                         window.location.port === '3006' ||
                         window.location.port === '3007' ||
                         window.location.port === '3008' ||
                         window.location.port === '3009' ||
                         window.location.port === '3010';

    this.container.innerHTML = `
      <div class="ologies-wrapper">
        <div class="ologies-content-container">
          <div class="ologies-header">
            <h1><span class="ologies-title">'Apps'</span> —our products: original, efficient, human.</h1>
          </div>
          <div class="ologies-error">
            <h2>Unable to Load Apps</h2>
            <p>${message}</p>
            ${isDevelopment ? `
              <p><strong>Development Setup:</strong></p>
              <ol>
                <li>Make sure the Astro server is running: <code>cd ologies-content && npm run dev</code></li>
                <li>Check that it's accessible at <a href="http://localhost:4321" target="_blank">http://localhost:4321</a></li>
                <li>Then reload this page</li>
              </ol>
            ` : `
              <p><strong>This appears to be a temporary issue.</strong></p>
              <p>Please try refreshing the page. If the problem persists, we're likely updating our systems.</p>
            `}
            <button onclick="window.location.reload()">Reload</button>
          </div>
        </div>
      </div>
    `;
  }

  // ---------------------------------------------------------------------
  // Fun view: pixel platformer — walk around, open doors to each creation
  // ---------------------------------------------------------------------

  switchView(view) {
    if (this.currentView === view) return;
    this.currentView = view;

    const basicEl = this.container.querySelector('#creations-basic-view');
    const funEl = this.container.querySelector('#creations-fun-view');
    const legendEl = this.container.querySelector('#creations-fun-legend');
    const toggleButtons = this.container.querySelectorAll('.creations-view-toggle-btn');

    toggleButtons.forEach(btn => {
      const isActive = btn.dataset.view === view;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
    });

    if (view === 'fun') {
      if (basicEl) basicEl.style.display = 'none';
      if (funEl) funEl.style.display = 'block';
      if (legendEl) legendEl.style.display = 'flex';
      if (funEl) {
        this.fitArenaToViewport(funEl);
        this.layoutDoors();
      }
      this.startGameLoop();
    } else {
      if (basicEl) basicEl.style.display = 'block';
      if (funEl) funEl.style.display = 'none';
      if (legendEl) legendEl.style.display = 'none';
      this.stopGameLoop();
    }
  }

  /**
   * Size the arena to whatever's actually visible above the fold of its
   * scrollable ancestor (not a blind vh guess), so nothing renders clipped
   * behind the terminal panel or off-screen on short mobile viewports.
   */
  fitArenaToViewport(el, { min = 360, max = 560 } = {}) {
    let ancestor = el.parentElement;
    while (ancestor) {
      const overflowY = getComputedStyle(ancestor).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') break;
      ancestor = ancestor.parentElement;
    }
    if (!ancestor) return;

    const ancestorRect = ancestor.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const availableBelow = ancestorRect.bottom - elRect.top - 12;

    el.style.height = `${Math.max(min, Math.min(max, availableBelow))}px`;
  }

  initFunView() {
    const container = this.container.querySelector('#creations-fun-view');
    if (!container) return;
    this.funContainer = container;
    this.fitArenaToViewport(container);

    container.innerHTML = `
      <div class="creations-river" id="creations-river"></div>
      <div class="creations-bridge" id="creations-bridge"></div>
      <div class="creations-doors-layer" id="creations-doors-layer"></div>
      <div class="creations-player" id="creations-player">
        <div class="creations-player__facing">
          <svg viewBox="0 0 16 20" class="creations-player__sprite" shape-rendering="crispEdges">
            <rect x="5" y="0" width="6" height="6" fill="var(--accent-green)"/>
            <rect x="3" y="6" width="10" height="8" fill="var(--accent-green)"/>
            <rect x="1" y="7" width="2" height="6" fill="var(--accent-green)"/>
            <rect x="13" y="7" width="2" height="6" fill="var(--accent-green)"/>
            <rect x="4" y="14" width="3" height="6" fill="var(--accent-green)"/>
            <rect x="9" y="14" width="3" height="6" fill="var(--accent-green)"/>
            <rect x="6" y="2" width="1" height="1" fill="#0c0c0c"/>
            <rect x="9" y="2" width="1" height="1" fill="#0c0c0c"/>
          </svg>
        </div>
      </div>
      <div class="creations-toast" id="creations-toast"></div>
      <div class="creations-controls" id="creations-controls">
        <div class="creations-dpad">
          <button type="button" class="creations-dpad__btn creations-dpad__btn--up" data-dir="up" aria-label="Move up">▲</button>
          <button type="button" class="creations-dpad__btn creations-dpad__btn--left" data-dir="left" aria-label="Move left">◀</button>
          <button type="button" class="creations-dpad__btn creations-dpad__btn--right" data-dir="right" aria-label="Move right">▶</button>
          <button type="button" class="creations-dpad__btn creations-dpad__btn--down" data-dir="down" aria-label="Move down">▼</button>
        </div>
        <button type="button" class="creations-action-btn" id="creations-action-btn" aria-label="Open door">SPACE</button>
      </div>
    `;

    this.player = {
      el: container.querySelector('#creations-player'),
      x: 0,
      y: 0,
      halfW: 8,
      halfH: 10,
    };

    this.setupTouchControls(container);

    this.layoutDoors();
    this.positionPlayerAtStart();
    this.applyPlayerTransform();

    if (this.currentView === 'fun') {
      this.startGameLoop();
    }
  }

  /**
   * On-screen d-pad + action button — mainly for touch devices, which have
   * no arrow keys or spacebar to drive the game with.
   */
  setupTouchControls(container) {
    const dirKeyMap = { up: 'up', down: 'down', left: 'left', right: 'right' };

    container.querySelectorAll('.creations-dpad__btn').forEach(btn => {
      const dir = dirKeyMap[btn.dataset.dir];
      if (!dir) return;

      const press = (e) => {
        e.preventDefault();
        this.keys[dir] = true;
      };
      const release = (e) => {
        e.preventDefault();
        this.keys[dir] = false;
      };

      btn.addEventListener('pointerdown', press);
      btn.addEventListener('pointerup', release);
      btn.addEventListener('pointerleave', release);
      btn.addEventListener('pointercancel', release);
      // Prevent the synthetic click a touch pointerdown/up pair would also
      // fire, and stop this from being treated as a focus/scroll target
      btn.addEventListener('contextmenu', (e) => e.preventDefault());
    });

    const actionBtn = container.querySelector('#creations-action-btn');
    if (actionBtn) {
      // iOS Safari only treats a trusted 'click' as real user activation for
      // window.open() — pointerdown/touchstart don't count, so a popup
      // triggered from those gets silently blocked on mobile.
      actionBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.tryOpenNearestDoor();
      });
      actionBtn.addEventListener('contextmenu', (e) => e.preventDefault());
    }
  }

  computeRiverGeometry(width, height) {
    const thickness = Math.max(24, Math.min(36, height * 0.06));
    // Sits lower than the midline, and runs on a diagonal (higher on one
    // side) so doors above it get more room and it reads less like a
    // straight ruled line.
    const baseTop = height * 0.72 - thickness / 2;
    const tilt = height * 0.07;
    const topLeft = baseTop - tilt / 2;
    const topRight = baseTop + tilt / 2;
    const bridgeWidth = Math.max(56, Math.min(76, width * 0.16));
    const bridgeX = width / 2;
    const bridgeTop = topLeft + (topRight - topLeft) * (bridgeX / width);

    return {
      width,
      thickness,
      topLeft,
      topRight,
      bottomLeft: topLeft + thickness,
      bottomRight: topRight + thickness,
      minTop: Math.min(topLeft, topRight),
      maxBottom: Math.max(topLeft, topRight) + thickness,
      bridgeX,
      bridgeWidth,
      bridgeLeft: bridgeX - bridgeWidth / 2,
      bridgeRight: bridgeX + bridgeWidth / 2,
      bridgeTop,
    };
  }

  riverTopAtX(x) {
    const r = this.river;
    const t = Math.min(Math.max(x / r.width, 0), 1);
    return r.topLeft + (r.topRight - r.topLeft) * t;
  }

  layoutDoors() {
    const container = this.funContainer;
    const doorsLayer = container.querySelector('#creations-doors-layer');
    const riverEl = container.querySelector('#creations-river');
    const bridgeEl = container.querySelector('#creations-bridge');
    if (!container || !doorsLayer) return;

    const width = container.clientWidth || 700;
    const height = container.clientHeight || 460;

    this.river = this.computeRiverGeometry(width, height);
    const river = this.river;

    if (riverEl) {
      // The river box spans the full tilt range; a clip-path polygon carves
      // the actual angled band out of it, so the bank can slope without the
      // element's own rectangle needing to.
      const boxTop = river.minTop;
      const boxHeight = river.maxBottom - river.minTop;
      const pct = (y) => `${(((y - boxTop) / boxHeight) * 100).toFixed(2)}%`;

      riverEl.style.top = `${boxTop}px`;
      riverEl.style.height = `${boxHeight}px`;
      riverEl.style.clipPath = `polygon(0% ${pct(river.topLeft)}, 100% ${pct(river.topRight)}, 100% ${pct(river.bottomRight)}, 0% ${pct(river.bottomLeft)})`;
    }
    if (bridgeEl) {
      bridgeEl.style.top = `${river.bridgeTop}px`;
      bridgeEl.style.height = `${river.thickness}px`;
      bridgeEl.style.left = `${river.bridgeLeft}px`;
      bridgeEl.style.width = `${river.bridgeWidth}px`;
    }

    const apps = this.apps;
    const count = apps.length;
    if (count === 0) {
      doorsLayer.innerHTML = '';
      this.doors = [];
      return;
    }

    // Doors only live above the river's shallowest point — the bridge is the
    // only way to reach them, and this guarantees no door can overlap the
    // river even at its highest (tilted) edge.
    const topAreaHeight = Math.max(1, river.minTop);
    const cols = Math.ceil(Math.sqrt(count));
    const rows = Math.ceil(count / cols);
    const cellW = width / cols;
    const cellH = topAreaHeight / rows;
    const doorHalfW = 22;
    const doorHalfH = 28;
    const marginX = Math.min(cellW * 0.22, 30);
    const marginBottom = Math.min(cellH * 0.18, 22);
    // Extra headroom for the always-visible label + prompt stacked above each
    // door — but never so much that it, plus the door itself, overflows a
    // short cell (that would push doors past the river boundary).
    const desiredMarginTop = Math.min(cellH * 0.35, 74);
    const maxMarginTop = Math.max(20, cellH - marginBottom - doorHalfH * 2 - 6);
    const marginTop = Math.min(Math.max(58, desiredMarginTop), maxMarginTop);

    doorsLayer.innerHTML = apps.map((app, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cellX = col * cellW;
      const cellY = row * cellH;

      const usableW = Math.max(1, cellW - marginX * 2 - doorHalfW * 2);
      const usableH = Math.max(1, cellH - marginTop - marginBottom - doorHalfH * 2);
      const jitterX = (Math.random() - 0.5) * usableW;
      const jitterY = (Math.random() - 0.5) * usableH;

      const x = cellX + cellW / 2 + jitterX;
      let y = cellY + marginTop + doorHalfH + (usableH / 2) + jitterY;
      // Hard guarantee, independent of the margin math above: no door's
      // bottom edge may cross the river's shallowest point.
      y = Math.min(y, topAreaHeight - doorHalfH - 4);

      const { data, slug } = app;
      const externalUrl = data.appStoreUrl || data.websiteUrl || '';
      const isComingSoon = !externalUrl;
      const cardImage = data.cardImage || '';
      const labelDelay = (Math.random() * 1.8).toFixed(2);

      return `
        <div class="creations-door ${isComingSoon ? 'creations-door--soon' : ''}" data-index="${i}" data-slug="${slug}" style="left:${x}px; top:${y}px;">
          <div class="creations-door__bubble">
            ${cardImage ? `<img src="${cardImage}" alt="${data.title} thumbnail" class="creations-door__bubble-thumb">` : ''}
          </div>
          <div class="creations-door__prompt">SPACE</div>
          <div class="creations-door__label" style="animation-delay:-${labelDelay}s;">${data.title}</div>
          <div class="creations-door__frame">
            <div class="creations-door__panel"></div>
            <div class="creations-door__glow"></div>
            <div class="creations-door__knob"></div>
          </div>
        </div>
      `;
    }).join('');

    this.doors = Array.from(doorsLayer.querySelectorAll('.creations-door')).map((el, i) => {
      const x = parseFloat(el.style.left);
      const y = parseFloat(el.style.top);
      const app = apps[i];
      const externalUrl = app.data.appStoreUrl || app.data.websiteUrl || '';
      return {
        el,
        x,
        y,
        radius: 30,
        near: false,
        opening: false,
        externalUrl,
        title: app.data.title,
      };
    });
  }

  positionPlayerAtStart() {
    if (!this.player || !this.funContainer) return;
    const width = this.funContainer.clientWidth || 700;
    const height = this.funContainer.clientHeight || 460;
    this.player.x = width / 2;
    this.player.y = height - this.player.halfH - 16;
  }

  startGameLoop() {
    this.stopGameLoop();
    const step = () => {
      if (!this.funContainer || !this.funContainer.isConnected) {
        this.animationFrame = null;
        return;
      }
      this.updateGame();
      this.animationFrame = requestAnimationFrame(step);
    };
    this.animationFrame = requestAnimationFrame(step);
  }

  stopGameLoop() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  updateGame() {
    if (!this.player || !this.funContainer) return;
    const SPEED = 2.8;

    let dx = 0;
    let dy = 0;
    if (this.keys.up) dy -= 1;
    if (this.keys.down) dy += 1;
    if (this.keys.left) dx -= 1;
    if (this.keys.right) dx += 1;

    const moving = dx !== 0 || dy !== 0;

    if (moving) {
      const len = Math.sqrt(dx * dx + dy * dy);
      dx = (dx / len) * SPEED;
      dy = (dy / len) * SPEED;

      const width = this.funContainer.clientWidth;
      const height = this.funContainer.clientHeight;

      let newX = Math.min(Math.max(this.player.x + dx, this.player.halfW), width - this.player.halfW);
      let newY = Math.min(Math.max(this.player.y + dy, this.player.halfH), height - this.player.halfH);

      const { x: resolvedX, y: resolvedY } = this.resolveRiverCollision(newX, newY);
      this.player.x = resolvedX;
      this.player.y = resolvedY;

      this.player.el.classList.add('creations-player--walking');
      if (dx < 0) this.player.el.classList.add('creations-player--facing-left');
      else if (dx > 0) this.player.el.classList.remove('creations-player--facing-left');
    } else {
      this.player.el.classList.remove('creations-player--walking');
    }

    this.applyPlayerTransform();
    this.updateDoorProximity();
  }

  applyPlayerTransform() {
    if (!this.player) return;
    const { el, x, y, halfW, halfH } = this.player;
    el.style.transform = `translate(${(x - halfW).toFixed(1)}px, ${(y - halfH).toFixed(1)}px)`;
  }

  /**
   * The river is a solid wall except at the bridge. The player can only
   * cross it — in either direction — while roughly aligned with the bridge.
   */
  resolveRiverCollision(newX, newY) {
    if (!this.river || !this.player) return { x: newX, y: newY };
    const { thickness, bridgeLeft, bridgeRight } = this.river;
    const { halfW, halfH } = this.player;

    const withinBridgeX = (x) => (x + halfW * 0.6) > bridgeLeft && (x - halfW * 0.6) < bridgeRight;
    const topAtOldX = this.riverTopAtX(this.player.x);
    const wasInBand = (this.player.y + halfH) > topAtOldX && (this.player.y - halfH) < topAtOldX + thickness;

    if (wasInBand) {
      // Mid-crossing: free to move along the river, but can't drift off the bridge sideways
      if (!withinBridgeX(newX)) {
        newX = Math.min(Math.max(newX, bridgeLeft + halfW), bridgeRight - halfW);
      }
      return { x: newX, y: newY };
    }

    const topAtNewX = this.riverTopAtX(newX);
    const willEnterBand = (newY + halfH) > topAtNewX && (newY - halfH) < topAtNewX + thickness;
    if (willEnterBand && !withinBridgeX(newX)) {
      const wasAbove = this.player.y <= topAtOldX;
      newY = wasAbove ? Math.min(newY, topAtNewX - halfH) : Math.max(newY, topAtNewX + thickness + halfH);
    }

    return { x: newX, y: newY };
  }

  updateDoorProximity() {
    if (!this.player || !this.doors.length) return;
    const PROXIMITY = 46;
    this.doors.forEach(door => {
      const dx = door.x - this.player.x;
      const dy = door.y - this.player.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const near = dist < PROXIMITY;
      if (near !== door.near) {
        door.near = near;
        door.el.classList.toggle('creations-door--near', near);
      }
    });
  }

  handleKeyDown(e) {
    if (!this.isFunViewActiveAndVisible()) return;

    const active = document.activeElement;
    const tag = active && active.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (active && active.isContentEditable)) return;

    const movementKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ', 'Spacebar'];
    if (movementKeys.includes(e.key)) e.preventDefault();

    if (e.key === 'ArrowUp') this.keys.up = true;
    else if (e.key === 'ArrowDown') this.keys.down = true;
    else if (e.key === 'ArrowLeft') this.keys.left = true;
    else if (e.key === 'ArrowRight') this.keys.right = true;
    else if (e.key === ' ' || e.key === 'Spacebar') this.tryOpenNearestDoor();
  }

  handleKeyUp(e) {
    if (e.key === 'ArrowUp') this.keys.up = false;
    else if (e.key === 'ArrowDown') this.keys.down = false;
    else if (e.key === 'ArrowLeft') this.keys.left = false;
    else if (e.key === 'ArrowRight') this.keys.right = false;
  }

  isFunViewActiveAndVisible() {
    if (this.currentView !== 'fun') return false;
    const tabContent = this.container.closest('.tabs__content');
    if (!tabContent) return true;
    return tabContent.isConnected && tabContent.classList.contains('active');
  }

  tryOpenNearestDoor() {
    const nearDoor = this.doors.find(d => d.near && !d.opening);
    if (!nearDoor) return;

    // window.open only counts as user-initiated — and so survives popup
    // blockers — when called synchronously inside the trusted event handler.
    // Deferring it into the animation's setTimeout (even by milliseconds)
    // makes browsers treat it as an unrequested popup and silently drop it,
    // so open the tab now and just point it at the URL once the door
    // animation finishes.
    let pendingTab = null;
    if (nearDoor.externalUrl) {
      pendingTab = window.open('', '_blank');
      if (pendingTab) pendingTab.opener = null;
    }

    this.openDoor(nearDoor, pendingTab);
  }

  openDoor(door, pendingTab) {
    door.opening = true;
    door.el.classList.add('creations-door--opening');

    setTimeout(() => {
      if (door.externalUrl) {
        if (pendingTab && !pendingTab.closed) {
          pendingTab.location.href = door.externalUrl;
        } else {
          // Blocked or closed before the animation finished — last resort
          window.open(door.externalUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        this.showToast(door, "Coming soon!");
      }
    }, 420);

    setTimeout(() => {
      door.el.classList.remove('creations-door--opening');
      door.opening = false;
    }, 1400);
  }

  showToast(door, message) {
    const toast = this.funContainer && this.funContainer.querySelector('#creations-toast');
    if (!toast) return;
    toast.textContent = message;
    toast.style.left = `${door.x}px`;
    toast.style.top = `${door.y - 60}px`;
    toast.classList.remove('creations-toast--visible');
    // Force reflow so the animation restarts if triggered again quickly
    void toast.offsetWidth;
    toast.classList.add('creations-toast--visible');
  }

  handleResize() {
    if (!this.funContainer) return;
    this.fitArenaToViewport(this.funContainer);
    this.layoutDoors();
    if (this.player && this.funContainer) {
      const width = this.funContainer.clientWidth;
      const height = this.funContainer.clientHeight;
      this.player.x = Math.min(Math.max(this.player.x, this.player.halfW), width - this.player.halfW);
      this.player.y = Math.min(Math.max(this.player.y, this.player.halfH), height - this.player.halfH);
      this.applyPlayerTransform();
    }
  }
}
